import React, { useMemo, useState, useEffect, useCallback } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useReportFormat from "../useReportFormat";

const yFormatter = (val) => {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${Math.round(val / 1_000)}K`;
  return String(Math.round(val));
};

const pickerSlotProps = {
  textField: {
    size: "small",
    sx: {
      width: 150,
      "& .MuiInputBase-root": { fontSize: "0.78rem", fontWeight: 500 },
      "& .MuiInputBase-input": { padding: "5px 8px" },
    },
  },
};

const RevenueFlowChart = ({ flow }) => {
  const formatCurrency = useReportFormat();
  const {
    monthly = [],
    collected = 0,
    scheduled = 0,
    unscheduled = 0,
    total_contracted = 0,
  } = flow || {};

  const sortedMonths = [...monthly].sort((a, b) => a.month.localeCompare(b.month));

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const lastMonth = sortedMonths[sortedMonths.length - 1]?.month ?? null;
  const availableMonths = useMemo(() => new Set(monthly.map((m) => m.month)), [monthly]);
  const shouldDisableMonth = useCallback((date) => !availableMonths.has(date.format("YYYY-MM")), [availableMonths]);

  const sortedAvailable = useMemo(() => Array.from(availableMonths).sort(), [availableMonths]);
  const defaultEndYM = lastMonth ?? dayjs().startOf("month").format("YYYY-MM");
  const targetStartYM = dayjs(defaultEndYM + "-01").subtract(6, "month").format("YYYY-MM");
  const defaultStartYM = sortedAvailable.find((m) => m >= targetStartYM) ?? sortedAvailable[0] ?? targetStartYM;

  const resetRange = useCallback(() => {
    setEndDate(dayjs(defaultEndYM + "-01"));
    setStartDate(dayjs(defaultStartYM + "-01"));
  }, [defaultEndYM, defaultStartYM]);

  useEffect(() => {
    resetRange();
  }, [resetRange]);

  const startYM = startDate?.format("YYYY-MM") ?? "";
  const endYM = endDate?.format("YYYY-MM") ?? "";
  const isFiltered = startYM !== defaultStartYM || endYM !== defaultEndYM;

  const visibleMonths = useMemo(() => {
    return sortedMonths.filter((m) => {
      if (startYM && m.month < startYM) return false;
      if (endYM && m.month > endYM) return false;
      return true;
    });
  }, [monthly, startYM, endYM]);

  const categories = visibleMonths.map((r) => {
    const [year, mon] = r.month.split("-").map(Number);
    return (
      new Date(year, mon - 1, 1).toLocaleString("en-US", { month: "short" }) +
      " " +
      year
    );
  });

  const series = [
    {
      data: visibleMonths.map((r) => Math.round(r.collected ?? 0)),
      label: "Collected",
      color: "#059669",
      valueFormatter: (v) => formatCurrency(v),
    },
    {
      data: visibleMonths.map((r) => Math.round(r.scheduled ?? 0)),
      label: "Scheduled",
      color: "#0891b2",
      valueFormatter: (v) => formatCurrency(v),
    },
  ];

  const collectedPct = total_contracted > 0 ? (collected / total_contracted) * 100 : 0;
  const scheduledPct = total_contracted > 0 ? (scheduled / total_contracted) * 100 : 0;
  const unscheduledPct = total_contracted > 0 ? (unscheduled / total_contracted) * 100 : 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div
        style={{
          background: "white",
          borderRadius: 16,
          padding: 32,
          border: "1px solid #f0f4ff",
          boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)",
          marginBottom: 28,
        }}
      >
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", color: "#002045", margin: 0, marginBottom: 2 }}>
              Revenue Pipeline
            </p>
            <p style={{ fontSize: "0.78rem", color: "#64748b", margin: "0", fontWeight: 500 }}>
              Monthly collected & scheduled payments
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
            <DatePicker
              label="From"
              views={["year", "month"]}
              openTo="month"
              value={startDate}
              maxDate={endDate ?? undefined}
              onChange={setStartDate}
              shouldDisableMonth={shouldDisableMonth}
              slotProps={pickerSlotProps}
            />
            <span style={{ fontSize: "0.78rem", color: "#94a3b8" }}>–</span>
            <DatePicker
              label="To"
              views={["year", "month"]}
              openTo="month"
              value={endDate}
              minDate={startDate ?? undefined}
              onChange={setEndDate}
              shouldDisableMonth={shouldDisableMonth}
              slotProps={pickerSlotProps}
            />
            {isFiltered && (
              <button
                type="button"
                onClick={resetRange}
                style={{
                  border: "none",
                  background: "none",
                  color: "#7c3aed",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "4px 2px",
                  textDecoration: "underline",
                  textUnderlineOffset: 2,
                }}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {visibleMonths.length === 0 ? (
          <div
            style={{
              minHeight: 280,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            No payment data yet
          </div>
        ) : (
          <BarChart
            series={series}
            xAxis={[{ data: categories, scaleType: "band" }]}
            yAxis={[{ valueFormatter: yFormatter }]}
            height={320}
            margin={{ top: 10, right: 20, bottom: 50, left: 60 }}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 0,
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                markGap: 6,
                itemGap: 16,
                labelStyle: { fontSize: 12, fontWeight: 500, fill: "#475569" },
              },
            }}
          />
        )}

        {/* Summary — always shows overall totals regardless of date filter */}
        <div style={{ paddingTop: 20, borderTop: "1px solid #f1f5f9" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Total Contracted
            </span>
            <span style={{ fontSize: "1rem", fontWeight: 800, color: "#002045" }}>
              {formatCurrency(total_contracted)}
            </span>
          </div>

          <div
            style={{
              height: 28,
              borderRadius: 6,
              overflow: "hidden",
              display: "flex",
              background: "#f1f5f9",
            }}
          >
            {collectedPct > 0 && (
              <div
                style={{
                  width: `${collectedPct}%`,
                  background: "#059669",
                  flexShrink: 0,
                  transition: "width 0.5s ease",
                }}
              />
            )}
            {scheduledPct > 0 && (
              <div
                style={{
                  width: `${scheduledPct}%`,
                  background: "#0891b2",
                  flexShrink: 0,
                  transition: "width 0.5s ease",
                }}
              />
            )}
            {unscheduledPct > 0 && (
              <div
                style={{
                  width: `${unscheduledPct}%`,
                  background: "#fbbf24",
                  flexShrink: 0,
                  transition: "width 0.5s ease",
                }}
              />
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 12,
              marginTop: 16,
            }}
          >
            {[
              { key: "Collected", val: collected, color: "#059669", pct: collectedPct, sub: "payments received" },
              { key: "Scheduled", val: scheduled, color: "#0891b2", pct: scheduledPct, sub: "date confirmed" },
              { key: "Expected", val: unscheduled, color: "#d97706", pct: unscheduledPct, sub: "no date yet" },
            ].map((seg) => (
              <div key={seg.key}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: seg.color,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "#64748b",
                    }}
                  >
                    {seg.key}
                  </span>
                </div>
                <div style={{ fontSize: "1rem", fontWeight: 800, color: "#002045" }}>
                  {formatCurrency(seg.val)}
                </div>
                <div style={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 500 }}>
                  {seg.pct.toFixed(1)}% · {seg.sub}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default RevenueFlowChart;
